/* eslint-disable jira/i18n/no-export-define-message */
import React, { useCallback } from 'react';
import { styled, type StyledProps } from '@compiled/react';
import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import { token } from '@atlaskit/tokens';
import {
	EntryKeys,
	ENTRYPOINT_ID_JIRA_PLATFORM,
} from '@atlassian/jira-feedback-collector/src/constants.tsx';
import { useFeedbackCollectorActions } from '@atlassian/jira-feedback-collector/src/controllers/index.tsx';
// eslint-disable-next-line no-restricted-imports
import { useIntl, defineMessages } from '@atlassian/jira-intl';
import { useCurrentRoute } from '@jira/platform__utils-router/src/index.tsx';
import normaliseUrl from '@jira/platform__utils-url-normalisation/src/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';

export const messages: {
	reasonPlaceholder: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	reasonQuestion: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	reasonBug: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	reasonComment: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	reasonOthers: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	fieldLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	feedbackTitleDetails: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	feedbackTitleDetailsQuestion: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	feedbackCancelButton: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	reasonPlaceholder: {
		id: 'workflow-editor-preference-settings.controllers.opt-out-feedback.reason-placeholder',
		defaultMessage: 'Select an option',
		description:
			'Placeholder of the dropdown element where users will be able to select a reason for opting out of the Workflow Editor',
	},
	reasonQuestion: {
		id: 'workflow-editor-preference-settings.controllers.opt-out-feedback.reason-question',
		defaultMessage: 'Missing features',
		description: 'A topic when providing feedback for missing features; option in dropdown.',
	},
	reasonBug: {
		id: 'workflow-editor-preference-settings.controllers.opt-out-feedback.reason-bug',
		defaultMessage: 'Bugs',
		description: 'A topic when providing feedback for buggy experience; option in dropdown.',
	},
	reasonComment: {
		id: 'workflow-editor-preference-settings.controllers.opt-out-feedback.reason-comment',
		defaultMessage: 'Prefer old editor',
		description:
			'A topic when leaving a comment that the old editor is preferred; option in dropdown.',
	},
	reasonOthers: {
		id: 'workflow-editor-preference-settings.controllers.opt-out-feedback.reason-others',
		defaultMessage: 'Other',
		description: 'A topic when providing feedback for generic reason; option in dropdown.',
	},
	fieldLabel: {
		id: 'workflow-editor-preference-settings.controllers.opt-out-feedback.field-label',
		defaultMessage: 'Add details to help us improve the workflow editor for everyone.',
		description:
			'Follow-up question after reason is given for changing default option to legacy workflow designer',
	},
	feedbackTitleDetails: {
		id: 'workflow-editor-preference-settings.controllers.opt-out-feedback.feedback-title-details',
		defaultMessage: 'Help us improve your experience with the new workflow editor.',
		description: 'Description of the feedback collector',
	},
	feedbackTitleDetailsQuestion: {
		id: 'workflow-editor-preference-settings.controllers.opt-out-feedback.feedback-title-details-question',
		defaultMessage: 'Let us know why you’re moving to the old editor',
		description: 'Description of the feedback collector options',
	},
	feedbackCancelButton: {
		id: 'workflow-editor-preference-settings.controllers.opt-out-feedback.feedback-cancel-button',
		defaultMessage: 'Skip step',
		description: 'Label for button that cancels sending feedback',
	},
});

type Props = {
	isAdminSettingsModal?: boolean;
	onClose?: () => void;
	projectType?: ProjectType;
};

type OpenFeedback = {
	renderFeedbackDialog: () => void;
};

type AdditionalEntries = {
	isSiteAdmin: boolean;
	routeName: string;
	projectType: string;
	cloudId: string;
};

const unknownAttributeValue = 'unknown';

const FEEDBACK_GROUP_ID = 'workflow-editor-opt-out';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const FeedbackTitleDetailsContainer: React.ComponentType<
	React.ClassAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement> & StyledProps
> = // eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
	styled.div({
		marginBottom: token('space.200'),
	});

export const getAdditionalFeedbackEntries = ({
	isSiteAdmin,
	routeName,
	projectType,
	cloudId,
}: AdditionalEntries): {
	id: string;
	value: string;
}[] => [
	{
		id: EntryKeys.LOCATION,
		value: normaliseUrl(globalThis.window?.location.href),
	},
	{
		id: EntryKeys.REFERER,

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		value: document.referrer ? normaliseUrl(document.referrer) : '',
	},
	{
		id: EntryKeys.USER_AGENT,
		value: globalThis.navigator?.userAgent,
	},
	{
		id: EntryKeys.SCREEN_RESOLUTION,
		value: `${globalThis.window?.screen?.width} x ${globalThis.window?.screen?.height}`,
	},
	{
		id: EntryKeys.ROUTE_NAME,
		value: routeName,
	},
	{
		id: EntryKeys.IS_ADMIN,
		value: isSiteAdmin.toString(),
	},
	{
		id: EntryKeys.PROJECT_TYPE,
		value: projectType,
	},
	{
		id: 'cloud-id',
		value: cloudId,
	},
];

export const useOptOutFeedbackDialog = ({
	onClose = () => undefined,
	projectType,
}: Props): OpenFeedback => {
	const { formatMessage } = useIntl();

	const { openFeedbackCollector } = useFeedbackCollectorActions();
	const route = useCurrentRoute();
	const { cloudId, isSiteAdmin } = useTenantContext();

	const routeName = route.name ?? unknownAttributeValue;

	const renderFeedbackDialog = useCallback(() => {
		openFeedbackCollector({
			entrypointId: ENTRYPOINT_ID_JIRA_PLATFORM,
			feedbackGroupId: FEEDBACK_GROUP_ID,
			onClose,
			feedbackContext: getAdditionalFeedbackEntries({
				isSiteAdmin,
				cloudId,
				routeName,
				projectType: projectType || unknownAttributeValue,
			}),
			feedbackGroupLabels: {
				empty: {
					selectOptionLabel: formatMessage(messages.reasonPlaceholder),
				},
				bug: {
					fieldLabel: formatMessage(messages.fieldLabel),
					selectOptionLabel: formatMessage(messages.reasonBug),
				},
				comment: {
					fieldLabel: formatMessage(messages.fieldLabel),
					selectOptionLabel: formatMessage(messages.reasonComment),
				},
				suggestion: {
					fieldLabel: formatMessage(messages.fieldLabel),
					selectOptionLabel: formatMessage(messages.reasonOthers),
				},
				question: {
					fieldLabel: formatMessage(messages.fieldLabel),
					selectOptionLabel: formatMessage(messages.reasonQuestion),
				},
			},
			feedbackTitleDetails: (
				<FeedbackTitleDetailsContainer>
					{formatMessage(messages.feedbackTitleDetails)}
				</FeedbackTitleDetailsContainer>
			),
			selectLabel: formatMessage(messages.feedbackTitleDetailsQuestion),
			cancelButtonLabel: formatMessage(messages.feedbackCancelButton),
		});
	}, [formatMessage, openFeedbackCollector, onClose, isSiteAdmin, cloudId, routeName, projectType]);

	return { renderFeedbackDialog };
};
